import { ApiError, type ApiClient } from '@ghp/api-client'
import type { AssignedInquiryPage, TourInternalMeetings, VocabularyEntry } from '@ghp/domain'

import { dropUnservedCarriedMedia, storeCarriedMedia, storeOfficeSchematics } from './carried-media'
import {
  SEND_QUEUES,
  cacheInquiries,
  replaceInternalMeetings,
  type DataDatabase,
  type OutboxEntry,
} from './data-db'
import { adoptFreeInspections } from './free-inspection'
import type { SharedDatabase } from './shared-db'

/** Le sous-ensemble injecté du client : cinq lectures, rien d'autre. */
export type PullApi = Pick<
  ApiClient['sync'],
  'listInquiries' | 'listInternalMeetings' | 'listVocabulary' | 'mediaPolicy' | 'downloadMedium'
>

/**
 * Pourquoi une descente n'a rien rendu. Les aplatir en un seul « indisponible » ferait dire à
 * l'écran « pas de réseau » à un agent dont la session est en fait refusée — il attendrait un
 * retour du réseau qui ne changerait rien.
 *
 * - `offline` — personne n'a répondu : le mode dégradé prévu (D4), rien à faire, la passe
 *   suivante suffit. Un `5xx` ou un `429` s'y range aussi : la conduite est la même, on rejoue ;
 * - `refused` — le serveur a dit non (401/403) : réessayer ne servira à rien, c'est la session
 *   qu'il faut refaire ;
 * - `malformed` — une réponse est arrivée, mais hors contrat : un bug à signaler, pas une panne ;
 * - `no_session` — aucun jeton en main, rien n'a été demandé.
 */
export type PullFailure = 'offline' | 'refused' | 'malformed' | 'no_session'

/**
 * Ce qu'une descente rend. **Une coupure n'est pas une erreur** ici : c'est le mode dégradé
 * prévu (D4), et l'appelant n'a rien à rattraper — le cache de la veille reste affiché, le
 * retour du réseau rappellera la fonction.
 *
 * `partial` existe pour ne pas avoir à choisir entre jeter ce qui est descendu et prétendre être
 * à jour : ce qui est arrivé est rangé, et l'indicateur « prêt pour le terrain » (D22 pt.3) sait
 * qu'il ne peut pas passer au vert.
 */
export type PullResult =
  | { outcome: 'updated'; count: number }
  | { outcome: 'partial'; count: number; reason: PullFailure }
  | { outcome: 'unavailable'; reason: PullFailure }

/**
 * Ce qu'on demande par page. Aligné sur le plafond du serveur (`MAX_PAGE_SIZE`,
 * `apps/api/src/inquiries/page-query.ts`) : demander plus serait ramené dans les bornes en
 * silence, demander moins multiplierait les allers-retours sur la 4G d'un chantier.
 */
const PAGE_SIZE = 200

/**
 * Garde-fou de boucle : 20 pages, soit 4 000 contrôles là où une tournée d'équipe en compte
 * quelques dizaines. Sans lui, un serveur qui rendrait un `total` énorme ou ignorerait l'`offset`
 * tiendrait la tablette en requêtes jusqu'à épuisement de la batterie.
 */
const MAX_PAGES = 20

/**
 * ⚠️ Le statut ne suffit pas à trancher. `http.ts` ne pose `cause` que sur un décodage raté
 * (JSON illisible, `ZodError`) — et une réponse hors contrat arrive avec un statut `200`. Un
 * refus HTTP, lui, porte `status` et `body`, jamais de `cause`. Ce qui n'est pas un `ApiError`
 * n'a pas atteint le serveur : `fetch` échoue en `TypeError`.
 */
function classifyFailure(error: unknown): PullFailure {
  if (!(error instanceof ApiError)) return 'offline'
  if (error.status === 401 || error.status === 403) return 'refused'

  return error.cause === undefined ? 'offline' : 'malformed'
}

/**
 * **Le `401` seulement, jamais le `403`** : un jeton mort se répare par un refresh — au
 * détenteur de la session de le savoir. Un `403` est un refus de rôle ou d'équipe, qu'un
 * refresh reproduirait à l'identique : le signaler ferait tourner refresh et refus en boucle
 * à chaque passe.
 */
function reportUnauthorized(
  error: unknown,
  accessToken: string,
  onUnauthorized?: (accessToken: string) => void,
): void {
  if (error instanceof ApiError && error.status === 401) onUnauthorized?.(accessToken)
}

/**
 * La tournée de l'équipe, descendue **avant le départ** (D22 pt.2) : un écran « Mes contrôles »
 * qui charge à la demande est un écran vide dès le premier sous-sol.
 *
 * ⚠️ Une tournée **complète** fait foi : ce qu'elle ne sert plus **quitte la tablette**
 * ({@link dropUnserved}, #668) — sans quoi une demande réaffectée, abandonnée ou effacée au
 * serveur y restait à vie. **Sauf** ce que l'agent a touché : une saisie, une pièce, un envoi pas
 * encore reçu, un contrôle libre. Ces demandes-là restent, et leur saisie est **notée hors
 * tournée** ({@link markDraftsOutOfTour}), seul moyen pour l'écran de la retrouver une fois sa
 * demande partie dans une autre équipe (#147).
 *
 * ⚠️ **Une page ne fait pas une tournée.** `GET /inquiries` en rend 50 par défaut : on demande
 * donc page après page jusqu'à `total`. Chaque page est rangée dès son arrivée — si la suivante
 * tombe, l'agent garde ce qui est descendu, et `partial` dit que la tournée n'est pas complète.
 *
 * ⚠️ **La tournée descend dans la base de l'équipe de la session** (D22 pt.4) — `db` est celle
 * que `offline/team-data.ts` tient ouverte. `null` tant qu'aucune session n'a nommé d'équipe :
 * la descente se conduit alors **comme sans jeton**, rien n'est demandé et rien n'est rangé.
 * Descendre sans savoir pour qui obligerait à choisir entre la base d'une autre équipe et une
 * base que personne n'ouvrira.
 */
export async function pullInquiries(
  db: DataDatabase | null,
  shared: SharedDatabase,
  api: PullApi,
  accessToken: string | null,
  onUnauthorized?: (accessToken: string) => void,
): Promise<PullResult> {
  if (!accessToken || db === null) {
    return { outcome: 'unavailable', reason: 'no_session' }
  }

  let count = 0
  const served = new Set<string>()
  // `null` tant que le serveur n'a pas dit ce qu'il attend : un serveur d'avant le champ.
  let awaiting: Set<string> | null = null

  for (let requested = 0; requested < MAX_PAGES; requested += 1) {
    let page: AssignedInquiryPage

    // ⚠️ Pris **avant** l'appel, et non à l'écriture : c'est l'instant où le serveur a été
    // interrogé, donc l'instant que la réponse décrit. Une descente partie avant un dépôt accepté
    // et atterrie après lui ressert une demande encore `in_progress` ; estampillée à l'écriture,
    // elle porterait une date postérieure au `sentAt` du dépôt et se lirait comme un renvoi au
    // terrain (`features/inspections/lib/list-filters.ts`). Le contrôle transmis repartirait dans
    // « à faire », définitivement : le serveur ne ressert plus une demande `submitted`, donc plus
    // rien ne bougerait cette date.
    const servedAt = new Date().toISOString()

    try {
      // L'offset suit ce qui est **reçu**, pas ce qui est demandé : le serveur plafonne `limit`
      // sans le dire, et une page plus courte que prévu ne doit pas faire sauter des demandes.
      page = await api.listInquiries(accessToken, { limit: PAGE_SIZE, offset: count })
    } catch (error) {
      reportUnauthorized(error, accessToken, onUnauthorized)
      const reason = classifyFailure(error)

      return count === 0
        ? { outcome: 'unavailable', reason }
        : { outcome: 'partial', count, reason }
    }

    // ⚠️ **La règle du serveur d'abord, avant même les lignes** : elle voyage sur l'enveloppe de
    // la tournée (M35), et c'est la seule occasion où la tablette l'apprend — la route des
    // réglages est réservée au bureau. Écrite à chaque page, ce qui ne coûte rien : une ligne,
    // remplacée par la même valeur.
    //
    // ⚠️ **Dans la base partagée, pas dans celle de l'équipe** : c'est une règle de GHP, la même
    // pour tous les binômes, pas une saisie d'équipe. Même régime que le référentiel d'anomalies.
    await shared.serverRules.put({
      id: 'current',
      allowAttachmentsAfterSubmission: page.allowAttachmentsAfterSubmission,
    })

    // La tournée est rangée dans la base de **la session**, pas dans celle que le serveur
    // désignerait : la vue servie au terrain ne porte pas d'équipe, et c'est le jeton qui a
    // décidé de son contenu.
    await cacheInquiries(db, page.items, servedAt)
    await adoptFreeInspections(db, page.items)
    // Les pièces qu'une contre-visite reprend (#675), pour qu'elles soient là hors réseau.
    await storeCarriedMedia(db, api, accessToken, page.items)
    // Le schéma corrigé au bureau d'un contrôle renvoyé (#437) : le brouillon en repart.
    await storeOfficeSchematics(db, api, accessToken, page.items)

    // Ce que le serveur sert encore, page après page : c'est le complément de cet ensemble que
    // `markDraftsOutOfTour` marquera et `dropUnserved` retirera, une fois la tournée complète.
    for (const inquiry of page.items) served.add(inquiry.id)
    if (page.awaitingValidation !== undefined) {
      awaiting = new Set([...(awaiting ?? []), ...page.awaitingValidation])
    }
    count += page.items.length

    // Une page vide clôt la descente même si `total` annonce plus : entre deux pages, une demande
    // a pu être réaffectée ou transmise. La tournée descendue est celle que le serveur sert
    // maintenant, pas celle qu'il comptait au début.
    if (page.items.length === 0 || count >= page.total) {
      await markDraftsOutOfTour(db, served)
      // ⚠️ **Dans cet ordre.** Les pièces reprises d'une contre-visite jamais ouverte (#675) ne
      // sont pas une saisie : elles partent d'abord, sans quoi `dropUnserved` les compterait
      // comme une pièce de l'agent et garderait la contre-visite à vie — le défaut de #668.
      await dropUnservedCarriedMedia(db, served)
      await dropUnserved(db, served, awaiting)

      return { outcome: 'updated', count }
    }
  }

  // Le plafond est atteint alors que le serveur en annonce encore : sa pagination est en cause,
  // pas le réseau. Ce qui est descendu reste rangé, et l'anomalie remonte comme un défaut.
  return { outcome: 'partial', count, reason: 'malformed' }
}

/**
 * Ce que la tournée ne porte plus est **noté sur le brouillon**, jamais effacé.
 *
 * Le bureau réaffecte une demande, la tournée redescend sans elle, et la saisie de l'équipe
 * sortante quitte « Mes contrôles » — qui part des demandes. Le relevé, lui, reste sur cette
 * tablette et nulle part ailleurs. Cette marque est le seul angle sous lequel un écran peut
 * le retrouver (#147) : la demande reste en cache ({@link dropUnserved} ne retire pas une demande
 * qui porte une saisie), « elle n'est plus là » ne s'y lit donc pas.
 *
 * Marque **réversible** : une demande rendue à l'équipe reprend sa ligne ordinaire, et sa
 * saisie ne doit plus être signalée comme sortie de la tournée.
 *
 * ⚠️ Sur une tournée **complète** seulement — l'appelant s'en assure. Sur une descente
 * interrompue, la page qui manque porte peut-être la demande : marquer là ferait passer une
 * coupure réseau pour une réaffectation.
 */
async function markDraftsOutOfTour(db: DataDatabase, served: ReadonlySet<string>): Promise<void> {
  const at = new Date().toISOString()

  // ⚠️ La base lue est celle de **l'équipe de cette descente** (D22 pt.4) : l'ensemble `served`
  // est celui de sa session, et le passer sur la tournée d'une autre équipe marquerait « sortie
  // de la tournée » des saisies que cette descente n'a aucune raison de juger. C'est la base
  // ouverte qui le garantit, plus aucun filtre.
  //
  // On n'écrit que les brouillons dont la situation change réellement : la descente est
  // fréquente, et réécrire une ligne inchangée réveillerait `liveQuery` à chaque passe.
  for (const draft of await db.drafts.toArray()) {
    // Un contrôle libre n'a jamais été servi : il n'a pas de tournée à quitter (M41).
    if ((await db.inquiries.get(draft.inquiryId))?.freeInspection !== undefined) continue

    const outOfTour = !served.has(draft.inquiryId)
    const marked = draft.outOfTourAt != null
    if (outOfTour === marked) continue

    await db.drafts.update(draft.inquiryId, { outOfTourAt: outOfTour ? at : null })
  }
}

/**
 * Retire de la tablette les demandes que la tournée complète ne sert plus (#668) — après une
 * remise à zéro au serveur, une réaffectation, un abandon.
 *
 * **Gardées**, parce qu'elles portent ce qui n'existe nulle part ailleurs (M21) :
 * - une **saisie** (`drafts`) — transmise comprise, tant que le bureau peut la renvoyer pour
 *   correction : c'est elle qui tient la ligne « transmis » de « Mes contrôles », et
 *   {@link markDraftsOutOfTour} la signale. ⚠️ Une saisie **au bureau en entier**
 *   ({@link receivedHandOvers}) que le serveur ne compte plus dans `awaiting` a été validée,
 *   supprimée ou confiée à une autre équipe : elle part avec sa demande et ses pièces, sans quoi
 *   chaque contrôle transmis restait à vie sur la tablette. Sans `awaiting` (serveur antérieur),
 *   elle reste ;
 * - une **pièce** (`media`) — une photo ne se reprend pas. ⚠️ Les copies qu'une contre-visite
 *   jamais ouverte reprend (`carriedFrom`, #675) ne sont pas des pièces de l'agent :
 *   `dropUnservedCarriedMedia` les retire **juste avant**, sans quoi elles garderaient la
 *   demande ;
 * - un **envoi en attente ou en vol**, dans l'une des quatre files : l'écran qui le montre relit
 *   la ligne de tournée. ⚠️ Un envoi **refusé pour de bon** (`failed`, `rejected`) ne la garde
 *   pas : il ne repartira pas vers une demande que le serveur ne sert plus, et il gardait à vie
 *   dans « À faire » un contrôle supprimé au bureau. Il reste dans sa file, donc dans
 *   « Notifications » ;
 * - un **contrôle libre** (M41), que le serveur n'a jamais servi.
 *
 * ⚠️ **Rien d'autre n'est retiré.** Les envois reçus restent dans leurs files — c'est l'historique
 * de « Notifications », qui dit déjà « Demande absente du cache de la tablette » quand la ligne
 * manque ; les points internes ont leur propre remplacement. Aucune autre table ne dépend d'une
 * demande sans valeur propre.
 *
 * ⚠️ **Une transaction** sur toutes les tables lues : une saisie ouverte pendant la descente
 * attend qu'elle se referme, au lieu de se retrouver sans sa demande.
 *
 * ⚠️ Sur une tournée **complète** seulement, pour la raison de {@link markDraftsOutOfTour}.
 */
async function dropUnserved(
  db: DataDatabase,
  served: ReadonlySet<string>,
  awaiting: ReadonlySet<string> | null,
): Promise<void> {
  const tables = [db.inquiries, db.drafts, db.media, ...SEND_QUEUES.map((queue) => db[queue])]

  await db.transaction('rw', tables, async () => {
    const received = awaiting === null ? new Set<string>() : await receivedHandOvers(db, awaiting)

    // Les pièces par leur index seul : lire les lignes chargerait chaque photo en mémoire.
    const touched = new Set<string>(
      [
        ...(await db.drafts.toCollection().primaryKeys()),
        ...((await db.media.orderBy('inquiryId').uniqueKeys()) as string[]),
      ].filter((inquiryId) => !received.has(inquiryId)),
    )
    for (const queue of SEND_QUEUES) {
      for (const entry of await db[queue].where('state').anyOf('pending', 'sending').toArray()) {
        touched.add(entry.inquiryId)
      }
    }

    const dropped = await db.inquiries
      .filter(
        (inquiry) =>
          !served.has(inquiry.id) &&
          !touched.has(inquiry.id) &&
          inquiry.freeInspection === undefined,
      )
      .primaryKeys()

    await db.inquiries.bulkDelete(dropped)
    // Seules les saisies reçues au bureau ont pu laisser partir leur demande : fiche et pièces
    // la suivent.
    await db.drafts.bulkDelete(dropped)
    await db.media.where('inquiryId').anyOf(dropped).delete()
  })
}

/**
 * Les saisies **au bureau en entier** que le serveur n'attend plus : verrouillées, dernier dépôt
 * reçu, chaque pièce montée — et hors d'`awaiting`, ce que le bureau peut encore renvoyer.
 */
async function receivedHandOvers(
  db: DataDatabase,
  awaiting: ReadonlySet<string>,
): Promise<Set<string>> {
  const latest = new Map<string, OutboxEntry>()
  for (const entry of await db.outbox.toArray()) {
    const current = latest.get(entry.inquiryId)
    if (current === undefined || entry.createdAt > current.createdAt) {
      latest.set(entry.inquiryId, entry)
    }
  }

  const received = new Set<string>()
  for (const draft of await db.drafts.toArray()) {
    if (draft.lockedAt == null || awaiting.has(draft.inquiryId)) continue
    if (latest.get(draft.inquiryId)?.state !== 'sent') continue
    const notUploaded = await db.media
      .where('inquiryId')
      .equals(draft.inquiryId)
      .filter((medium) => medium.uploadedAt == null)
      .count()
    if (notUploaded === 0) received.add(draft.inquiryId)
  }

  return received
}

/**
 * Les points internes de l'équipe (M28) — réunions, briefs, passages à l'atelier que le bureau
 * pose sur sa ligne du planning (#319). Sans eux, l'agenda de la tablette montre libre un créneau
 * où l'équipe est attendue au dépôt.
 *
 * ⚠️ **Dans la base de l'équipe, comme la tournée** : ce sont les créneaux d'**une** équipe, et
 * le binôme suivant de la tablette n'a pas à voir ceux du précédent. D'où le même `null` que
 * {@link pullInquiries} tant qu'aucune session n'a nommé d'équipe.
 *
 * ⚠️ **Remplacés, et non ajoutés** — sur la fenêtre que le serveur annonce seulement
 * (`replaceInternalMeetings`). Pas de pagination : une équipe en compte quelques-uns par semaine,
 * et la fenêtre servie est bornée côté serveur.
 */
export async function pullInternalMeetings(
  db: DataDatabase | null,
  api: PullApi,
  accessToken: string | null,
  onUnauthorized?: (accessToken: string) => void,
): Promise<PullResult> {
  if (!accessToken || db === null) {
    return { outcome: 'unavailable', reason: 'no_session' }
  }

  let served: TourInternalMeetings

  try {
    served = await api.listInternalMeetings(accessToken)
  } catch (error) {
    reportUnauthorized(error, accessToken, onUnauthorized)
    return { outcome: 'unavailable', reason: classifyFailure(error) }
  }

  await replaceInternalMeetings(db, served)

  return { outcome: 'updated', count: served.items.length }
}

/**
 * Le référentiel d'anomalies (M17, M18), descendu en entier et gardé : sans lui, le sélecteur
 * d'anomalies ne propose rien, et une fiche saisie sans vocabulaire ne se rattrape pas.
 *
 * ⚠️ **Dans la base partagée, pas dans celle d'une équipe** (`offline/shared-db.ts`), et c'est
 * volontaire : le référentiel n'est la saisie de personne, et en donner un exemplaire par équipe
 * le ferait redescendre à chaque changement de main — le sélecteur d'anomalies d'un agent hors
 * réseau resterait vide toute la tournée. D'où aussi le jeton seul en paramètre, là où
 * `pullInquiries` demande la base de l'équipe.
 *
 * ⚠️ Ici on **remplace**, contrairement à la tournée. Le serveur ne sert que les entrées actives :
 * ce qui n'est plus servi a été désactivé en back-office, et une valeur désactivée qui reste
 * proposée finit **figée dans une fiche** (M19 recopie le texte à la saisie). L'argument qui
 * protège les demandes ne vaut pas pour un référentiel, qui n'est la saisie de personne.
 */
export async function pullVocabulary(
  db: SharedDatabase,
  api: PullApi,
  accessToken: string | null,
  onUnauthorized?: (accessToken: string) => void,
): Promise<PullResult> {
  if (!accessToken) return { outcome: 'unavailable', reason: 'no_session' }

  let entries: VocabularyEntry[]

  try {
    entries = await api.listVocabulary(accessToken)
  } catch (error) {
    reportUnauthorized(error, accessToken, onUnauthorized)
    return { outcome: 'unavailable', reason: classifyFailure(error) }
  }

  // ⚠️ Un référentiel vide ne remplace rien. Le seul cas où cette descente peut vider le
  // sélecteur d'anomalies est aussi celui où elle est le plus probablement fautive (mauvaise
  // route, filtre serveur cassé) — et un agent sans vocabulaire ne peut plus rien qualifier.
  if (entries.length === 0 && (await db.vocabulary.count()) > 0) {
    return { outcome: 'partial', count: 0, reason: 'malformed' }
  }

  // La même transaction pour le retrait et l'ajout : entre les deux, le sélecteur d'anomalies ne
  // doit jamais voir un référentiel amputé.
  await db.transaction('rw', db.vocabulary, async () => {
    const served = new Set(entries.map((entry) => entry.id))
    const withdrawn = (await db.vocabulary.toCollection().primaryKeys()).filter(
      (id) => !served.has(id),
    )

    await db.vocabulary.bulkDelete(withdrawn)
    await db.vocabulary.bulkPut(entries)
  })

  return { outcome: 'updated', count: entries.length }
}
